import { ModelContract, ModuleDescription } from "../../shared/module-description";
import { IUserAuthContract } from "../../student/auth/model";
import { createEffect, createEvent, createStore, sample } from "effector";
import { CompletedCourseRequestRecord, ICourse } from "../../shared/entities/courses";
import { reset } from "patronum";
import { toastShow } from "@features/notification/model";
import { ICustomError } from "@features/shared/entities/notification";
import { CourseDataApi } from "@features/shared/api/course-data.api";
import { CourseCompleted } from "@features/shared/events";
import { IPlatformContextContract } from "@features/launch-context/model";
import { contentLocaleChanged } from "@features/shared/locale";
import { IAmplitudeFlagsContract } from "@features/amplitude-flags/model";

export const coursesLoaded = createEvent();
export const draftCoursesLoaded = createEvent();
export const completeCourse = createEvent<CompletedCourseRequestRecord>();

export const $courses = createStore<ICourse[]>([], { skipVoid: false });
export const $allCourses = createStore<ICourse[]>([], { skipVoid: false });
export const $draftCourses = createStore<ICourse[]>([], { skipVoid: false });
export const $completedCourse = createStore<CompletedCourseRequestRecord | null>(null);

export const getCoursesFx = createEffect(async () => {
    const response = await CourseDataApi.getCourses();
    return response.sort((a, b) => a.orderNumber - b.orderNumber);
});
// Starts true: courses are "loading" from app start until the first fetch
// completes — the initial fetch is gated on the flags identity settling (see
// run()), and consumers like /next-lesson treat `!loading && empty` as "the
// student really has no courses", which must not fire during that gate window.
export const $coursesLoading = createStore(true).on(getCoursesFx.pending, (_, pending) => pending);

export const getDraftCoursesFx = createEffect(async () => {
    const response = await CourseDataApi.getAllCoursesIncludingDrafts();
    return response.sort((a, b) => a.orderNumber - b.orderNumber);
});

export const courseCompletedFx = createEffect(async (data: CompletedCourseRequestRecord) => {
    const response = await CourseDataApi.completeCourse(data);
    return response;
});

const trackCourseCompletedFx = createEffect(
    ({ course_id, course_name, course_type }: { course_id: number; course_name: string; course_type: string }) => {
        CourseCompleted(course_id, course_name, course_type);
    }
);

sample({
    source: {
        courses: $courses,
        completed: $completedCourse
    },
    clock: courseCompletedFx.done,
    filter: ({ completed }) => completed !== null,
    fn: ({ courses, completed }) => {
        const c = completed!;
        const course = courses.find((course) => course.id === c.courseId);
        return {
            course_id: c.courseId,
            course_name: course?.title ?? "unknown",
            course_type: course?.type ?? "course"
        };
    },
    target: trackCourseCompletedFx
});

sample({
    clock: completeCourse,
    target: [courseCompletedFx, $completedCourse]
});

sample({
    clock: [getCoursesFx.fail, courseCompletedFx.fail],
    filter: ({ error }: { error: ICustomError }) => error?.error?.title !== null,
    fn: ({ error }: { error: ICustomError }) => error?.error,
    target: toastShow
});

sample({
    clock: getDraftCoursesFx.doneData,
    target: [$draftCourses, draftCoursesLoaded]
});

const contract = new ModelContract(
    {
        $courses,
        $allCourses,
        $draftCourses,
        $coursesLoading
    },
    {
        coursesLoaded,
        draftCoursesLoaded,
        completeCourse
    },
    {
        getCoursesFx,
        getDraftCoursesFx,
        courseCompletedFx
    },
    {}
);

export type ICoursesContract = typeof contract;

export class CoursesModule extends ModuleDescription<ICoursesContract> {
    constructor(
        public userAuthModule: IUserAuthContract,
        public launchContextModule: IPlatformContextContract,
        // optional so existing tests that don't exercise the remap keep working
        public amplitudeFlagsModule?: IAmplitudeFlagsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        const iosTitleReplacements: Array<{ from: string; to: string }> = [
            {
                from: "$100K in 2026: Your Personal AI Income Challenge",
                to: "Your Personal AI Challenge"
            },
            {
                from: "Your Personal AI Income Challenge",
                to: "Your Personal AI Challenge"
            },
            {
                from: "Personal AI Income Challenge",
                to: "Your Personal AI Challenge"
            },
        ];

        // The course list is remapped by the X-Course-Remap header, which is
        // only applied after the post-login flag fetch settles — fetching on
        // bare `authenticated` would load unmapped courses and then reload
        // them (visible flash of wrong-variant courses). So the initial fetch
        // waits for BOTH auth and $identityFlagsSettled, whichever lands last;
        // $coursesMap.updates re-fires it if the remap ever changes later.
        // The `pending` guard dedupes the clocks firing in the same cycle.
        if (this.amplitudeFlagsModule) {
            sample({
                clock: [
                    this.userAuthModule.events.authenticated,
                    this.amplitudeFlagsModule.stores.$identityFlagsSettled.updates,
                    this.amplitudeFlagsModule.stores.$coursesMap.updates
                ],
                source: {
                    student: this.userAuthModule.stores.$student,
                    settled: this.amplitudeFlagsModule.stores.$identityFlagsSettled,
                    pending: getCoursesFx.pending
                },
                filter: ({ student, settled, pending }) => Boolean(student) && settled && !pending,
                fn: () => undefined,
                target: getCoursesFx
            });
        } else {
            sample({
                clock: this.userAuthModule.events.authenticated,
                target: getCoursesFx
            });
        }

        // Course titles/descriptions are translated by the API — refetch them when
        // the language changes so the cached previous locale is not shown.
        sample({
            clock: contentLocaleChanged,
            filter: this.userAuthModule.stores.$student.map((student) => Boolean(student)),
            fn: () => undefined,
            target: getCoursesFx
        });

        sample({
            clock: getCoursesFx.doneData,
            target: $allCourses
        });

        sample({
            source: {
                platform: this.launchContextModule.stores.$platform,
                user: this.userAuthModule.stores.$student
            },
            clock: getCoursesFx.doneData,
            fn: (s, p) => {
                if (s.platform === "web") {
                    return p;
                }

                const userHasSubscription = s.user?.status === "active" || s.user?.status === "redemption";
                let filteredCourses = userHasSubscription
                    ? p
                    : p.filter((it) => it.availableInTrial);

                if (s.platform === "ios") {
                    filteredCourses = filteredCourses.map((course) => {
                        const replacement = iosTitleReplacements.find((r) => r.from === course.title);
                        if (replacement) {
                            return { ...course, title: replacement.to };
                        }
                        return course;
                    });
                }

                return filteredCourses;
            },
            target: [$courses, coursesLoaded]
        });

        reset({
            clock: this.userAuthModule.events.logoutStudent,
            // $coursesLoading returns to its initial `true` so the next login
            // waits for the gated first fetch again instead of reading
            // "loaded and empty".
            target: [$courses, $allCourses, $draftCourses, $coursesLoading]
        });
    }
}
